/**
 * KeyboardDriver — desktop dev input. Throttle/brake ramp like real pedals;
 * shifting and mode toggles fire callbacks.
 *
 *  ↑ / W   throttle        → / E / Space  shift up      M  auto/manual gearbox
 *  ↓ / S   brake           ← / Q          shift down    K  auto-drive / keys
 *  I / Enter  ENG START / STOP     L  launch control
 */
export interface KeyboardHandlers {
  onShiftUp?: () => void;
  onShiftDown?: () => void;
  onToggleGearbox?: () => void;
  onToggleDrive?: () => void;
  onIgnition?: () => void;
  onLaunch?: () => void;
}

export class KeyboardDriver {
  pedal = 0;
  brake = 0;
  private throttleDown = false;
  private brakeDown = false;
  private attached = false;

  constructor(private readonly h: KeyboardHandlers = {}) {}

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    window.addEventListener('keydown', this.onDown);
    window.addEventListener('keyup', this.onUp);
    window.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    window.removeEventListener('keydown', this.onDown);
    window.removeEventListener('keyup', this.onUp);
    window.removeEventListener('blur', this.onBlur);
  }

  /** Call each frame; pedals ramp for realism. */
  tick(dtS: number): void {
    const up = dtS / 0.18;
    const down = dtS / 0.12;
    this.pedal = this.throttleDown ? Math.min(1, this.pedal + up) : Math.max(0, this.pedal - down);
    this.brake = this.brakeDown ? Math.min(1, this.brake + up * 1.5) : Math.max(0, this.brake - down);
  }

  get anyPressed(): boolean {
    return this.throttleDown || this.brakeDown;
  }

  private onDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    if (isEditable(e.target)) return;
    switch (e.code) {
      case 'ArrowUp':
      case 'KeyW':
        this.throttleDown = true;
        e.preventDefault();
        break;
      case 'ArrowDown':
      case 'KeyS':
        this.brakeDown = true;
        e.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyE':
      case 'Space':
        this.h.onShiftUp?.();
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'KeyQ':
        this.h.onShiftDown?.();
        e.preventDefault();
        break;
      case 'KeyM':
        this.h.onToggleGearbox?.();
        break;
      case 'KeyK':
        this.h.onToggleDrive?.();
        break;
      case 'KeyI':
      case 'Enter':
        this.h.onIgnition?.();
        e.preventDefault();
        break;
      case 'KeyL':
        this.h.onLaunch?.();
        break;
      default:
        break;
    }
  };

  private onUp = (e: KeyboardEvent): void => {
    switch (e.code) {
      case 'ArrowUp':
      case 'KeyW':
        this.throttleDown = false;
        break;
      case 'ArrowDown':
      case 'KeyS':
        this.brakeDown = false;
        break;
      default:
        break;
    }
  };

  private onBlur = (): void => {
    this.throttleDown = false;
    this.brakeDown = false;
  };
}

// only fields you TYPE into keep the keys: a slider the admin SIM's VOL fader left focused used to
// swallow every arrow (no gas, no gears, the volume moved instead)
const NOT_TEXT = new Set(['range', 'checkbox', 'radio', 'button', 'submit', 'reset', 'color']);

function isEditable(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.tagName === 'INPUT') return !NOT_TEXT.has((el as HTMLInputElement).type);
  return el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}
